import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ClipboardJS from 'clipboard';
import { QRCodeCanvas } from 'qrcode.react';

// Renders a QR code that points at the user's public profile URL, plus the
// "Download QR Code" and "Copy Link" actions. Embedded in the "Share my Profile"
// modal (see layouts/frontend/navbar/_share_profile_modal.html.erb).
//
// The QR is only generated once its modal is opened (it sits on every authenticated
// page, but most page views never open it). qrcode.react 3.x's QRCodeCanvas is a plain
// function component and does NOT forward refs, so we read the rendered <canvas> from a
// wrapper element rather than via a ref on QRCodeCanvas.
function ProfileQr({
  profileUrl, logoUrl, size, downloadFileName,
}) {
  const rootRef = useRef(null);
  const qrWrapRef = useRef(null);
  const copyButtonRef = useRef(null);
  const copyTimerRef = useRef(null);
  const [active, setActive] = useState(false);
  const [logoReady, setLogoReady] = useState(!logoUrl);
  const [copied, setCopied] = useState(false);

  // logoReady is seeded from logoUrl only on first render; if logoUrl changes on a live
  // instance (async/late props, component reuse), re-gate so Download can't fire before the
  // new logo is loaded and drawn into the canvas.
  useEffect(() => {
    setLogoReady(!logoUrl);
  }, [logoUrl]);

  // Defer QR generation until the enclosing modal is (about to be) shown. Falls back to
  // rendering immediately if there is no modal ancestor (e.g. rendered standalone).
  useEffect(() => {
    const modalEl = rootRef.current && rootRef.current.closest('.modal');
    if (!modalEl || modalEl.classList.contains('show')) {
      setActive(true);
      return undefined;
    }
    const onShow = () => setActive(true);
    modalEl.addEventListener('show.bs.modal', onShow);
    return () => modalEl.removeEventListener('show.bs.modal', onShow);
  }, []);

  // Preload the logo so the canvas is fully drawn (logo included) before the user can
  // download it. Never block the download if the logo fails to load.
  useEffect(() => {
    if (!active || !logoUrl) return undefined;

    let cancelled = false;
    const img = new Image();
    const markReady = () => { if (!cancelled) setLogoReady(true); };
    img.onload = markReady;
    img.onerror = markReady;
    img.src = logoUrl;

    return () => { cancelled = true; };
  }, [active, logoUrl]);

  // Copy Link uses ClipboardJS (already a project dependency, cf. copy_text.jsx) so the
  // execCommand fallback and browser quirks are handled in one place. The `container` must
  // be the modal element: Bootstrap traps focus inside the modal, so ClipboardJS's temporary
  // textarea has to live there or the copy silently fails.
  useEffect(() => {
    if (!active || !copyButtonRef.current) return undefined;

    const container = copyButtonRef.current.closest('.modal') || undefined;
    const clipboard = new ClipboardJS(copyButtonRef.current, {
      text: () => profileUrl,
      container,
    });
    clipboard.on('success', (e) => {
      e.clearSelection();
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    });

    return () => clipboard.destroy();
  }, [active, profileUrl]);

  // Clear any pending "Copied!" reset timer if we unmount first (e.g. Turbolinks nav).
  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  const imageSettings = logoUrl && logoReady
    ? {
      src: logoUrl,
      height: Math.round(size * 0.24),
      width: Math.round(size * 0.24),
      excavate: true,
    }
    : undefined;

  const downloadQrCode = () => {
    const canvas = qrWrapRef.current && qrWrapRef.current.querySelector('canvas');
    if (!canvas) return;

    let url;
    try {
      url = canvas.toDataURL('image/png');
    } catch (e) {
      // Should not happen now the logo is inlined as a data URI, but report if it ever does.
      window.Rollbar?.error('Unable to export QR code image', e);
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div ref={rootRef} className="d-flex flex-column align-items-center w-100">
      <div ref={qrWrapRef}>
        {active && (
          <QRCodeCanvas
            value={profileUrl}
            size={size}
            level="H"
            bgColor="#ffffff"
            fgColor="#212528"
            imageSettings={imageSettings}
          />
        )}
      </div>

      <div className="d-flex gap-3 w-100 mt-4">
        <button
          type="button"
          className="share-profile-action-btn"
          onClick={downloadQrCode}
          disabled={!logoReady}
        >
          <i className="fa fa-download" />
          Download QR Code
        </button>
        <button
          type="button"
          ref={copyButtonRef}
          className="share-profile-action-btn"
        >
          <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} />
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>
    </div>
  );
}

ProfileQr.propTypes = {
  profileUrl: PropTypes.string.isRequired,
  logoUrl: PropTypes.string,
  size: PropTypes.number,
  downloadFileName: PropTypes.string,
};

ProfileQr.defaultProps = {
  logoUrl: undefined,
  size: 172,
  downloadFileName: 'qwoted-profile-qr.png',
};

export default ProfileQr;
